import { ChangeEvent } from 'react';
import { action, computed, IObservableArray, makeObservable, observable, when } from 'mobx';
import { JobSpeciality } from '@clipr/lib';
import { documentDragErrorMessage, isDocumentFile, Nullable, pluralize, SUPPORTED_DOCUMENT_TYPES_LABEL } from '../../core';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, InputState } from '../../components/input';
import { createArray } from '../../core/array';
import { JobSynchronizer } from '../../entities/job/jobSynchronizer';
import { jobIdFromToken, recordingLocationFromToken } from '../../services/upload/uploadToken';
import Routes, { BackRoutes } from '../../routes';
import { WarrantUploadFileItemState } from './warrantUploadFileItemState';

type Props = BindingProps<{
  teamId?: string;
}>;

export type WarrantDocumentType = 'primary' | 'supplemental';

export const WARRANT_DOCUMENT_TYPE_ITEMS = [
  { value: 'primary', label: 'Primary narrative' },
  { value: 'supplemental', label: 'Supplemental narrative' },
];

export class WarrantUploadState
  extends StoreNode {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      teamId: computed,
      isHovered: observable,
      isDragHovered: observable,
      isSubmitting: observable,
      primaryItemId: observable,
      isDisabled: computed,
      showStagingForm: computed,
      isSubmitDisabled: computed,
      setPrimary: action,
      setDocumentType: action,
      dragError: computed,
      dragErrorMessage: computed,
      dragDropMessage: computed,
      handlePointerEnter: action,
      handlePointerLeave: action,
      handleFileChange: action,
      handleFileClick: action,
      handleDragEnter: action,
      handleDragLeave: action,
      handleDrop: action,
      setUploadInput: action,
      addFileItem: action,
      removeFileItem: action,
      submit: action,
      reset: action,
    });

    this.detectiveInsightsInput = input(this, {
      name: 'detectiveInsights',
      label: `Add additional Detective insights`,
      placeholder: `Add additional Detective insights`,
      multiline: true,
    });
  }

  readonly detectiveInsightsInput: InputState;

  get teamId(): Nullable<string> {
    return this.resolvedProps.teamId;
  }

  isHovered: boolean = false;
  isDragHovered: boolean = false;
  isSubmitting: boolean = false;
  primaryItemId: Nullable<string> = null;
  readonly dragItems = observable.array<DataTransferItem>();

  readonly jobId: Nullable<string> = null;

  get isDisabled(): boolean {
    return this.isSubmitting;
  }

  readonly stagedItems: IObservableArray<WarrantUploadFileItemState> =
    createArray<WarrantUploadFileItemState>(true) as IObservableArray;

  get showStagingForm(): boolean {
    return this.stagedItems.length > 0;
  }

  get isSubmitDisabled(): boolean {
    return this.isSubmitting || this.stagedItems.length === 0;
  }

  isPrimary(id: string): boolean {
    return this.primaryItemId === id;
  }

  documentTypeValue(id: string): WarrantDocumentType {
    return this.isPrimary(id) ? 'primary' : 'supplemental';
  }

  setPrimary(id: string) {
    this.primaryItemId = id;
  }

  // Exactly one patrol narrative. Promoting a file demotes the previous primary;
  // demoting the current primary promotes the next staged file so one always remains.
  setDocumentType(id: string, type: WarrantDocumentType) {
    if (type === 'primary') {
      this.primaryItemId = id;
      return;
    }
    if (this.primaryItemId === id) {
      const next = this.stagedItems.find(item => item.id !== id);
      this.primaryItemId = next?.id ?? null;
    }
  }

  get dragError(): boolean {
    return !!this.dragErrorMessage;
  }

  get dragErrorMessage(): Nullable<string> {
    return documentDragErrorMessage([...this.dragItems]);
  }

  get dragDropMessage(): Nullable<string> {
    const items = [...this.dragItems];
    if (items.length === 0)
      return `Drop files here`;
    return `Drop ${pluralize(items.length, 'file', 'files')} here`;
  }

  handlePointerEnter(_evt: PointerEvent) {
    this.isHovered = true;
  }
  handlePointerLeave(_evt: PointerEvent) {
    this.isHovered = false;
  }
  handleFileChange(evt: ChangeEvent<HTMLInputElement>) {
    this.setUploadInput(evt.target.files);
    // @ts-expect-error -- resetting input.value to null clears the file selection; string type doesn't allow null
    evt.target.value = null;
  }
  handleFileClick(_evt: PointerEvent) { /* noop */ }

  handleDragEnter(evt: DragEvent) {
    this.isDragHovered = true;
    const items = evt.dataTransfer?.items;
    if (items && items.length > 0) {
      this.dragItems.replace([...items]);
    }
  }
  handleDragLeave(_evt: DragEvent) {
    this.isDragHovered = false;
  }
  handleDrop(_evt: DragEvent) {
    this.isDragHovered = false;
    this.dragItems.clear();
  }

  setUploadInput(fileList?: FileList | null) {
    if (!fileList || fileList.length === 0)
      return;

    const inputFiles = [...fileList];

    const nonDocumentFiles = inputFiles.filter(file => !isDocumentFile(file));
    if (nonDocumentFiles.length > 0) {
      this.dispatch(
        'NotificationService',
        'notifyError',
        pluralize(
          nonDocumentFiles.length,
          `file is not a supported document (${SUPPORTED_DOCUMENT_TYPES_LABEL})`,
          `files are not supported documents (${SUPPORTED_DOCUMENT_TYPES_LABEL})`));
      return;
    }

    inputFiles.forEach(file => this.addFileItem(file));
  }

  addFileItem(file: File) {
    const item = new WarrantUploadFileItemState(this.store, { file });
    this.stagedItems.push(item);
    this.store.documentUploadGuidedTraining.handleDocumentFileSelected('warrant');
    // First staged document defaults to the patrol narrative; the user can reassign.
    if (!this.primaryItemId)
      this.primaryItemId = item.id;
  }

  removeFileItem(id: string) {
    const itemIndex = this.stagedItems.findIndex(item => item.id === id);
    if (itemIndex > -1)
      this.stagedItems.splice(itemIndex, 1);
    if (this.primaryItemId === id)
      this.primaryItemId = this.stagedItems[0]?.id ?? null;
  }

  async submit(): Promise<string | null> {
    if (this.isSubmitDisabled)
      return null;

    this.isSubmitting = true;

    const teamId = this.teamId || undefined;
    const primaryItem = this.stagedItems.find(item => this.isPrimary(item.id)) ?? this.stagedItems[0];
    const supplementalItems = this.stagedItems.filter(item => item.id !== primaryItem.id);

    // Upload the primary narrative first so the server mints the shared job id; every
    // supplemental then uploads under that id at {dataLocation}/document/source/{index}-{name}.
    const [primaryTask] = this.store.uploadService.createTasks(
      [{ file: primaryItem.file, jobSpeciality: JobSpeciality.WarrantFirstDraft, uploadOnly: true, partIndex: 0 }],
      { teamId },
    );
    await when(() => primaryTask.isCompleted || primaryTask.isFailed);

    const primaryToken = primaryTask.uploadedToken;
    const jobId = jobIdFromToken(primaryToken);

    if (primaryTask.isFailed || !primaryToken || !jobId) {
      this.dispatch('NotificationService', 'notifyError', 'Failed to start warrant narrative.');
      this.isSubmitting = false;
      return null;
    }

    const supplementalTasks = this.store.uploadService.createTasks(
      supplementalItems.map((item, index) => ({
        file: item.file,
        jobSpeciality: JobSpeciality.WarrantFirstDraft,
        uploadOnly: true,
        partIndex: index + 1,
      })),
      { teamId, jobId },
    );

    await Promise.all(supplementalTasks.map(task => when(() => task.isCompleted || task.isFailed)));

    if (supplementalTasks.some(task => task.isFailed)) {
      this.dispatch('NotificationService', 'notifyError', 'Failed to upload one or more documents.');
      this.isSubmitting = false;
      return null;
    }

    const inputDocumentLocations = [primaryTask, ...supplementalTasks]
      .map(task => recordingLocationFromToken(task.uploadedToken))
      .filter((location): location is string => !!location);

    const synchronizer = new JobSynchronizer({
      teamId,
      jobData: {
        speciality: JobSpeciality.WarrantFirstDraft,
        inputDocumentLocations,
        detectiveInsights: this.detectiveInsightsInput.value || undefined,
      },
    }, this.store);
    synchronizer.bindToken(primaryToken);

    const [job, jobErr] = await synchronizer.submitStartJob();

    if (jobErr || !job) {
      this.dispatch('NotificationService', 'notifyError', 'Failed to start warrant narrative.');
      this.isSubmitting = false;
      return null;
    }

    this.reset();
    this.isSubmitting = false;
    this.store.documentUploadGuidedTraining.handleWorkflowDocumentStarted('warrant', job.id, teamId ?? null);

    const { state, ...to } = BackRoutes[Routes.userDocument()].route({ id: job.id, teamId });
    this.store.history.push(to, state);
    return job.id;
  }

  reset() {
    this.stagedItems.clear();
    this.primaryItemId = null;
    this.detectiveInsightsInput.clear();
  }
}
